import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface OrderFilter {
  selectedCategoriesFilter: string[];
  selectedPropertiesFilter: string[];
  searchOrderTerm: string;
  gridView: boolean;
}

interface OrderFiltersState {
  // State
  filters: OrderFilter;

  // Actions
  setFilters: (newFilters: Partial<OrderFilter>) => void;
  clearFilters: () => void;
  setGridView: (gridView: boolean) => void;
  setSearchTerm: (term: string) => void;
  setCategoryFilters: (categories: string[]) => void;
  setPropertyFilters: (properties: string[]) => void;
}

const initialFilters: OrderFilter = {
  selectedCategoriesFilter: [],
  selectedPropertiesFilter: [],
  searchOrderTerm: "",
  gridView: true,
};

// DevTools configuration options
const devToolsOptions = {
  // Name of the store in Redux DevTools (helps when you have multiple stores)
  name: "OrderFilters",

  // Enable/disable DevTools based on environment (optional)
  // You could use process.env.NODE_ENV !== 'production' here
  enabled: true,

  // Custom anonymous action type (optional)
  anonymousActionType: "UNKNOWN_ACTION",

  // Serialize options (optional - used for handling complex objects)
  serialize: {
    // Options for handling circular references
    options: true,
  },
};

// State persists across React Router navigations (module singleton) and
// resets naturally on page refresh — no localStorage needed.
export const useOrderFiltersStore = create<OrderFiltersState>()(
  // DevTools should be the outermost middleware
  devtools(
    (set) => ({
      // Initial state
      filters: initialFilters,

      // Actions
      setFilters: (newFilters) =>
        // The third parameter to set allows us to name the action for DevTools
        set(
          (state) => ({
            filters: { ...state.filters, ...newFilters },
          }),
          false,
          { type: "order-filters/setFilters", payload: newFilters }
        ),

      clearFilters: () =>
        set({ filters: initialFilters }, false, "order-filters/clearFilters"),

      setGridView: (gridView) =>
        set(
          (state) => ({
            filters: { ...state.filters, gridView },
          }),
          false,
          { type: "order-filters/setGridView", payload: gridView }
        ),

      setSearchTerm: (searchOrderTerm) =>
        set(
          (state) => ({
            filters: { ...state.filters, searchOrderTerm },
          }),
          false,
          {
            type: "order-filters/setSearchTerm",
            payload: searchOrderTerm,
          }
        ),

      setCategoryFilters: (selectedCategoriesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedCategoriesFilter },
          }),
          false,
          {
            type: "order-filters/setCategoryFilters",
            payload: selectedCategoriesFilter,
          }
        ),

      setPropertyFilters: (selectedPropertiesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedPropertiesFilter },
          }),
          false,
          {
            type: "order-filters/setPropertyFilters",
            payload: selectedPropertiesFilter,
          }
        ),
    }),
    // Pass DevTools options as the second parameter to devtools
    devToolsOptions
  )
);
